import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectIsEditMode } from 'src/app/analysis/state/analysis.selectors';
import { SeraTableLoader } from '../sera-table/models/SeraTableLoader';
import { SeraAttachmentsLogicService } from '../../services/sera-attachments/sera-attachments-logic.service';
import { SeraButton } from '../sera-table/models/SeraCustomActions';
import { AttachmentLevel } from 'src/app/analysis/models/attachment/AttachmentLevel';
import { SeraTableLoaderAction } from '../../services/sera-attachments/models/SeraTableLoaderAction';
import { tap } from 'rxjs';
import { Attachment } from '../../../analysis/models/attachment/Attachment';
import { MM13OpInstruction } from '../../../analysis/cr13/models/MM13OpInstruction';
import { deleteFile } from 'src/app/analysis/state/analysis.actions';

@Component({
    selector: 'sera-attachments',
    templateUrl: './sera-attachments.component.html',
    styleUrls: ['./sera-attachments.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SeraAttachmentsComponent implements OnInit, OnDestroy {
    @Input()
    attachmentLevel: AttachmentLevel = AttachmentLevel.Analysis;

    @Input()
    attachmentsTableContent!: SeraTableLoader;

    @Input()
    attachmentsData!: Attachment[] | undefined | MM13OpInstruction[];

    @Input()
    toastErrorLabel: string = '';

    @Input()
    criterionToChange?: string | null = null;

    @Output()
    closePopupEvent: EventEmitter<boolean> = new EventEmitter();

    @Output()
    saveAttachmentsEvent = new EventEmitter<void>();

    isOpInstructionAttachment: boolean = false;
    isEditMode: boolean = false;

    $analysisIsEditMode = this.store.select(selectIsEditMode).pipe(
        tap(editMode => {
            this.isEditMode = editMode;
            this.setRowDisabledOrEnabled(editMode);
        })
    );

    constructor(private store: Store, private cdr: ChangeDetectorRef, public attachmentsLogicService: SeraAttachmentsLogicService) {}

    ngOnInit(): void {
        this.setActionsForIcons();
        this.isOpInstructionAttachment = this.attachmentLevel === AttachmentLevel.OperatingInstruction;
        this.attachmentsTableContent = this.attachmentsLogicService.handleAttachments(this.attachmentsData, this.attachmentsTableContent, this.attachmentLevel);
    }

    setActionsForIcons() {
        for (const icon of this.attachmentsTableContent.toolbarButtons!) {
            this.setActionForIcon(icon);
        }
        for (const icon of this.attachmentsTableContent.cols) {
            icon.seraButtons?.forEach(icon => {
                this.setActionForIcon(icon);
            });
        }
    }

    addAttachment() {
        this.attachmentsLogicService.addAttachment(this.attachmentsTableContent, this.attachmentLevel);
    }

    onSubmit() {
        this.saveAttachmentsEvent.emit();
        this.attachmentsLogicService.onSubmit(
            this.attachmentsTableContent,
            this.attachmentLevel,
            this.toastErrorLabel,
            this.closePopupEvent,
            this.criterionToChange
        );
    }

    deleteRow(data: { rowData: any; rowIndex: number }) {
        this.attachmentsTableContent.data.splice(data.rowIndex, 1);
        if (data.rowData.tmpUuid) {
            this.store.dispatch(deleteFile({ fileId: data.rowData.tmpUuid }));
        }
    }

    getLinkOrFile(data: { rowData: any; rowIndex: number }) {
        switch (this.attachmentLevel) {
            case AttachmentLevel.ForceCase:
            case AttachmentLevel.Analysis:
            case AttachmentLevel.CR1:
            case AttachmentLevel.CR2:
            case AttachmentLevel.CR3:
            case AttachmentLevel.CR4:
            case AttachmentLevel.CR5:
            case AttachmentLevel.CR6:
            case AttachmentLevel.CR7:
            case AttachmentLevel.CR8:
            case AttachmentLevel.CR9:
            case AttachmentLevel.CR10:
            case AttachmentLevel.CR11:
            case AttachmentLevel.CR12:
            case AttachmentLevel.CR13:
            case AttachmentLevel.HAZARD:
                this.attachmentsLogicService.getLinkOrFileFromNormalAttachments(data);
                break;
            case AttachmentLevel.OperatingInstruction:
                this.attachmentsLogicService.getLinkOrFileForOpInstructions(data);
                break;
        }
    }

    handleFileInput(event: any, rowData: any) {
        switch (this.attachmentLevel) {
            case AttachmentLevel.ForceCase:
            case AttachmentLevel.Analysis:
            case AttachmentLevel.CR1:
            case AttachmentLevel.CR2:
            case AttachmentLevel.CR3:
            case AttachmentLevel.CR4:
            case AttachmentLevel.CR5:
            case AttachmentLevel.CR6:
            case AttachmentLevel.CR7:
            case AttachmentLevel.CR8:
            case AttachmentLevel.CR9:
            case AttachmentLevel.CR10:
            case AttachmentLevel.CR11:
            case AttachmentLevel.CR12:
            case AttachmentLevel.CR13:
            case AttachmentLevel.HAZARD:
                this.attachmentsLogicService.handleFileInputForNormalAttachments(this.cdr, event, rowData);
                break;
            case AttachmentLevel.OperatingInstruction:
                this.attachmentsLogicService.handleFileInputForOpInstruction(this.cdr, event, rowData);
                break;
        }
    }

    setActionForIcon(icon: SeraButton) {
        switch (icon.actionType) {
            case SeraTableLoaderAction.Add:
                icon.action = this.addAttachment.bind(this);
                break;
            case SeraTableLoaderAction.Delete:
                icon.action = this.deleteRow.bind(this);
                break;
            case SeraTableLoaderAction.GetLink:
                icon.action = this.getLinkOrFile.bind(this);
                break;
        }
    }

    setRowDisabledOrEnabled(isInEditMode: boolean) {
        for (const col of this.attachmentsTableContent.cols) {
            col.isDisabled = !isInEditMode;
        }
    }

    ngOnDestroy(): void {
        this.attachmentsLogicService.unsubscribeOfSubscription();
    }
}
